import {useFrame} from '@react-three/fiber';
import {useEffect, useState} from 'react';
import {Vector3} from 'three';
import {TrainingTarget} from '../../components/app/TrainingTarget';
import {
  VR_POSITIONS,
  createPeripheralPositions,
  createTargetPosition,
  getDistanceValue,
  getSpeedValue,
} from '../../constants/vrPositions';
import {useExerciseTime} from '../../hooks/useExerciseTime';
import {ballPositionStore, useXRController} from '../../hooks/useXRController';
import {useStore} from '../../store/useStore';
import {DistanceSetting, ExerciseSettings, RangeSetting} from '../../types/exercises';
import {logger} from '../../utils/logger';

export const AccommodationExercise: React.FC<ExerciseSettings> = ({
  eyeToTrain = 'both',
  isRedGreen = false,
  isMFBF = false,
  isOverlay = false,
  leftEyeColor,
  rightEyeColor,
  leftEyeOverlay,
  rightEyeOverlay,
  speed = 7,
  range = 'medium',
  distance = 'middle',
  ballPosition,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [time, setTime] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Use the hook to get time remaining (Keep this, but ensure useExerciseTime respects global pause)
  const timeRemaining = useExerciseTime();

  // Get the global timer pause action from Zustand store
  const toggleTimerPause = useStore(state => state.toggleTimerPause);

  // Use the centralized XR controller hook
  const {isInXR, handlePositionChange} = useXRController();

  // Handle menu toggle - update local pause state AND global timer pause state
  const onMenuToggle = (isOpen: boolean) => {
    setIsPaused(isOpen);
    toggleTimerPause(isOpen);
    logger.info(`AccommodationExercise: Menu toggled, global pause set to ${isOpen}`);
  };

  // Get base distance value
  const baseDistance = getDistanceValue(distance);
  const [leftPeripheral, rightPeripheral] = createPeripheralPositions(baseDistance);

  // Create a function to generate positions for the accommodation exercise
  const getRangePositions = (rangeSetting: RangeSetting, distanceSetting: DistanceSetting): Vector3[] => {
    const localBaseDistance = getDistanceValue(distanceSetting);
    const positions: Vector3[] = [];
    let step: number;

    // Calculate step size between each position based on range setting
    switch (rangeSetting) {
      case 'narrow':
        step = 0.25;
        break;
      case 'medium':
        step = 0.5;
        break;
      case 'wide':
        step = 0.75;
        break;
      default:
        step = 0.5;
    }

    // Create 5 evenly spaced positions from near to far
    for (let i = -2; i <= 2; i++) {
      const z = localBaseDistance + i * step;
      positions.push(createTargetPosition(0, VR_POSITIONS.EXERCISE.TARGET_HEIGHT, z));
    }

    return positions;
  };

  // Apply settings to movement parameters
  const switchInterval = getSpeedValue(speed) * 2; // Slower switching for accommodation
  const targetPositions = getRangePositions(range, distance);

  useEffect(() => {
    logger.info('AccommodationExercise mounted');
    setIsInitialized(true);
    return () => {
      logger.info('AccommodationExercise unmounted');
    };
  }, []);

  // Initialize ball position from saved settings
  useEffect(() => {
    if (ballPosition) {
      logger.info('Initializing ball position from saved settings:', ballPosition);
      ballPositionStore.setOffset(ballPosition);
    }
  }, [ballPosition]);

  useFrame((_, delta) => {
    // Skip updates if not initialized or paused
    if (!isInitialized || isPaused) {
      return;
    }

    // Update time for switching
    setTime(prev => prev + delta);

    // Switch between positions based on time
    if (time > switchInterval) {
      setTime(0);
      const index = (activeIndex + 1) % targetPositions.length;
      setActiveIndex(index);
    }
  });

  return (
    <group>
      {/* Main training target - switches between positions */}
      <TrainingTarget
        position={targetPositions[activeIndex]}
        showPeripheral={false}
        eyeToTrain={eyeToTrain}
        isRedGreen={isRedGreen}
        isMFBF={isMFBF}
        isOverlay={isOverlay}
        leftEyeColor={leftEyeColor}
        rightEyeColor={rightEyeColor}
        leftEyeOverlay={leftEyeOverlay}
        rightEyeOverlay={rightEyeOverlay}
        inXRSession={isInXR}
        onPositionChange={handlePositionChange}
        onMenuToggle={onMenuToggle}
        timeRemaining={timeRemaining}
      />

      {/* Show all positions as smaller targets if in MFBF mode */}
      {isMFBF && (
        <>
          {targetPositions.map((position, index) => (
            <TrainingTarget
              key={index}
              position={position}
              scale={index === activeIndex ? 0.5 : 0.2}
              showPeripheral={true}
              eyeToTrain={eyeToTrain}
              isRedGreen={isRedGreen}
              isMFBF={isMFBF}
              isOverlay={isOverlay}
              inXRSession={isInXR}
            />
          ))}

          {/* Additional peripheral targets */}
          <TrainingTarget
            position={leftPeripheral}
            scale={0.5}
            showPeripheral={true}
            eyeToTrain={eyeToTrain}
            isRedGreen={isRedGreen}
            isMFBF={isMFBF}
            isOverlay={isOverlay}
            inXRSession={isInXR}
          />
          <TrainingTarget
            position={rightPeripheral}
            scale={0.5}
            showPeripheral={true}
            eyeToTrain={eyeToTrain}
            isRedGreen={isRedGreen}
            isMFBF={isMFBF}
            isOverlay={isOverlay}
            inXRSession={isInXR}
          />
        </>
      )}
    </group>
  );
};
